// CT Meter — Spindle current monitoring (current transformer reading).
// Live amperage, baseline comparison, strip chart, anomaly alerts.
import React, { useEffect, useState, useRef } from 'react';
import {
  Box, Card, Typography, Button, TextField, MenuItem, Stack, Chip, LinearProgress,
} from '@mui/material';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { customerbasedshift, shiftadd } from '../../Services/app/masterservice';
import { cleanCustomerId } from '../../Services/app/operatorservice';

const STORAGE_KEY = 'ct_meter_log';
const MACHINES = ['ATECH-V01', 'ATECH-V02', 'ATECH-V03', 'ATECH-V05', 'ATECH-V06', 'ATECH-V07', 'ATECH-V08', 'ATECH-T01', 'ATECH-T02', 'ATECH-T04'];

const SAMPLES = 60; // strip-chart length

const CtMeterScreen = () => {
  const cid = cleanCustomerId(localStorage.getItem('CustomerID'));
  const [machine, setMachine] = useState('ATECH-V07');
  const [baseline, setBaseline] = useState(12.5);
  const [nominalMax, setNominalMax] = useState(18.0);
  const [criticalMax, setCriticalMax] = useState(22.0);
  const [history, setHistory] = useState([]); // anomaly log
  const [chart, setChart] = useState(Array(SAMPLES).fill(0));
  const [current, setCurrent] = useState(0);
  const [tone, setTone] = useState('green');
  const tickRef = useRef(0);

  // Live simulated reading — sine wave + drift + occasional spike
  useEffect(() => {
    const id = setInterval(() => {
      tickRef.current += 1;
      const t = tickRef.current;
      // Wave: baseline + slow drift + small noise; spike every 25 ticks
      const wave = Math.sin(t / 7) * 2.4;
      const drift = Math.sin(t / 42) * 1.2;
      const noise = (Math.sin(t * 1.7) + Math.cos(t * 2.3)) * 0.3;
      const spike = (t % 25 === 0) ? 6 : 0;
      const reading = Math.max(0, baseline + wave + drift + noise + spike);
      setCurrent(reading);
      setChart(prev => [...prev.slice(1), reading]);
      const newTone = reading >= criticalMax ? 'red' : reading >= nominalMax ? 'amber' : 'green';
      setTone(newTone);
      if (reading >= criticalMax && t % 8 === 0) {
        // log critical event
        setHistory(prev => {
          const next = [{ id: 'CT-' + Date.now(), ts: Date.now(), machine, current: reading, tone: 'critical' }, ...prev].slice(0, 100);
          shiftadd({ [STORAGE_KEY]: next }, cid, 'SERVER_SCOPE').catch(() => {});
          return next;
        });
      }
    }, 600);
    return () => clearInterval(id);
  }, [baseline, nominalMax, criticalMax, machine, cid]);

  // Load prior log on mount + on machine change
  useEffect(() => {
    (async () => {
      try { const r = await customerbasedshift(cid, STORAGE_KEY); setHistory(Array.isArray(r?.[0]?.value) ? r[0].value : []); } catch { setHistory([]); }
    })();
  }, [cid]);

  const toneColor = tone === 'red' ? '#dc2626' : tone === 'amber' ? '#f59e0b' : '#16a34a';
  const toneBg = tone === 'red' ? '#fee2e2' : tone === 'amber' ? '#fef3c7' : '#dcfce7';
  const pctOfMax = Math.min(100, (current / (criticalMax * 1.1)) * 100);

  // Strip chart as inline SVG (no extra deps)
  const w = 640, h = 140;
  const maxY = Math.max(criticalMax * 1.15, Math.max(...chart));
  const path = chart.map((v, i) => {
    const x = (i / (SAMPLES - 1)) * w;
    const y = h - (v / maxY) * h;
    return (i === 0 ? 'M' : 'L') + x.toFixed(1) + ',' + y.toFixed(1);
  }).join(' ');
  const normalY = h - (nominalMax / maxY) * h;
  const critY = h - (criticalMax / maxY) * h;
  const baseY = h - (baseline / maxY) * h;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <ElectricBoltIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>CT Meter · Spindle Current Monitor</Typography>
        <Chip label={`${history.length} anomalies logged`} size="small" sx={{ ml: 'auto' }} />
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {/* Live reading */}
        <Card sx={{ p: 2.5, flex: 1, borderRadius: 3, border: `4px solid ${toneColor}`, bgcolor: toneBg, transition: 'all 200ms ease' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: toneColor, letterSpacing: 1.2 }}>LIVE SPINDLE CURRENT</Typography>
          <Stack direction="row" alignItems="baseline" sx={{ mt: 1, mb: 1.5 }}>
            <Typography sx={{ fontSize: 76, fontWeight: 900, color: toneColor, lineHeight: 1, fontFamily: 'monospace' }}>
              {current.toFixed(1)}
            </Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 800, color: toneColor, ml: 1.5 }}>A</Typography>
            <Box sx={{ ml: 'auto' }}>
              {tone === 'red' && <Chip icon={<WarningAmberIcon />} label="CRITICAL — STOP CUT" color="error" sx={{ fontWeight: 900 }} />}
              {tone === 'amber' && <Chip label="HIGH — CHECK TOOL" color="warning" sx={{ fontWeight: 900 }} />}
              {tone === 'green' && <Chip label="NORMAL" color="success" sx={{ fontWeight: 900 }} />}
            </Box>
          </Stack>
          <LinearProgress variant="determinate" value={pctOfMax} sx={{
            height: 14, borderRadius: 7, bgcolor: 'background.paper',
            '& .MuiLinearProgress-bar': { bgcolor: toneColor, borderRadius: 7 },
          }} />
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>0 A</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#f59e0b' }}>nominal {nominalMax} A</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>critical {criticalMax} A</Typography>
          </Stack>
        </Card>

        {/* Setup card */}
        <Card sx={{ p: 2.5, width: { xs: '100%', md: 340 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'text.secondary', letterSpacing: 1.2, mb: 2 }}>SETUP</Typography>
          <Stack spacing={2}>
            <TextField select label="Machine" value={machine} onChange={(e) => setMachine(e.target.value)} size="small">
              {MACHINES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
            <TextField label="Baseline (A)" type="number" value={baseline} onChange={(e) => setBaseline(Number(e.target.value) || 0)} size="small" inputProps={{ step: 0.1 }} />
            <TextField label="Nominal max (A)" type="number" value={nominalMax} onChange={(e) => setNominalMax(Number(e.target.value) || 0)} size="small" inputProps={{ step: 0.1 }} />
            <TextField label="Critical max (A)" type="number" value={criticalMax} onChange={(e) => setCriticalMax(Number(e.target.value) || 0)} size="small" inputProps={{ step: 0.1 }} />
          </Stack>
        </Card>
      </Stack>

      {/* Strip chart */}
      <Card sx={{ p: 2.5, mt: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.secondary', letterSpacing: 1 }}>
            LAST {SAMPLES} READINGS · {machine}
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2, fontSize: 11 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 3, bgcolor: '#6b7280' }} />
              <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 700 }}>baseline</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 3, bgcolor: '#f59e0b' }} />
              <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 700 }}>nominal</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 3, bgcolor: '#dc2626' }} />
              <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 700 }}>critical</Typography>
            </Box>
          </Box>
        </Stack>
        <Box sx={{ width: '100%', overflow: 'hidden' }}>
          <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
            {/* Threshold lines */}
            <line x1="0" y1={baseY} x2={w} y2={baseY} stroke="#6b7280" strokeWidth="1" strokeDasharray="4 4" />
            <line x1="0" y1={normalY} x2={w} y2={normalY} stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="6 4" />
            <line x1="0" y1={critY} x2={w} y2={critY} stroke="#dc2626" strokeWidth="1.5" strokeDasharray="6 4" />
            {/* Trace */}
            <path d={path} fill="none" stroke={toneColor} strokeWidth="2.5" strokeLinejoin="round" />
            {/* Recent point */}
            <circle cx={w} cy={h - (current / maxY) * h} r="5" fill={toneColor} />
          </svg>
        </Box>
      </Card>

      {/* Anomaly log */}
      <Card sx={{ p: 2, mt: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.secondary', mb: 1.5, letterSpacing: 1 }}>
          ANOMALY LOG (critical events)
        </Typography>
        {history.length === 0 && <Typography sx={{ color: 'text.disabled', fontSize: 13, py: 2 }}>No critical events recorded.</Typography>}
        {history.slice(0, 20).map(h => (
          <Box key={h.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Chip label="CRITICAL" size="small" sx={{ bgcolor: '#fee2e2', color: '#7a0000', fontWeight: 800 }} />
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13 }}>{h.machine}</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: '#dc2626', ml: 'auto' }}>
              {Number(h.current).toFixed(1)} A
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.disabled', minWidth: 130, textAlign: 'right' }}>
              {new Date(h.ts).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'medium' })}
            </Typography>
          </Box>
        ))}
      </Card>
    </Box>
  );
};

export default CtMeterScreen;
